import React, { useEffect, useState, useRef } from "react";
import services from "../../services";
import moment from "moment";
import { toast } from "react-toastify";
import { LuSendHorizontal } from "react-icons/lu";
import { MdAttachFile, MdRefresh } from "react-icons/md";
import { Typography } from "@mui/material";

const ConversationTab = ({ ticketId }) => {
    const [ticketData, setTicketData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [replyMessage, setReplyMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);

    const isInitialLoad = useRef(true);
    const scrollContainerRef = useRef(null);
    const [isAtBottom, setIsAtBottom] = useState(true);

    const scrollToBottom = (behavior = "smooth") => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior });
        }
    };

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
            // If user is within 50px of the bottom, consider them 'at bottom'
            const atBottom = scrollHeight - scrollTop - clientHeight < 50;
            setIsAtBottom(atBottom);
        }
    };

    const fetchTicket = async (isInitial = false) => {
        if (!ticketId) {
            setLoading(false);
            return;
        }
        try {
            if (isInitial) setLoading(true);
            const response = await services.support.getTicketById(ticketId);
            if (response.success) {
                setTicketData(response.data);
            } else {
                setError("Failed to fetch ticket details");
            }
        } catch (err) {
            console.error("Error fetching ticket:", err);
            setError(err.message || "An error occurred");
        } finally {
            if (isInitial) setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchTicket();
        setRefreshing(false);
    };

    useEffect(() => {
        // Initial fetch
        isInitialLoad.current = true;
        fetchTicket(true);
    }, [ticketId]);

    useEffect(() => {
        if (ticketData) {
            if (isInitialLoad.current) {
                // Initial load: instant jump to bottom
                setTimeout(() => {
                    scrollToBottom("auto");
                    isInitialLoad.current = false;
                    setIsAtBottom(true);
                }, 100);
            } else if (isAtBottom) {
                // Polling update: only scroll if user was already at the bottom
                scrollToBottom("smooth");
            }
        }
    }, [ticketData, isAtBottom]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            toast.info(`Selected file: ${file.name}`);
        }
    };

    const handleSendReply = async () => {
        if (!replyMessage.trim() && !selectedFile) {
            toast.error("Please enter a message or select a file");
            return;
        }

        try {
            setSending(true);
            let fileUrl = "";

            // 1. Upload file if exists
            if (selectedFile) {
                const formData = new FormData();
                formData.append("image", selectedFile);
                const uploadRes = await services.support.uploadSingleFile(formData);
                // Extract image name from response
                fileUrl = uploadRes?.data?.image || "";
            }

            // 2. Prepare payload
            const userData = JSON.parse(localStorage.getItem("userData") || "{}");
            const userRole = userData.role || "admin";

            const payload = {
                description: [
                    {
                        message: replyMessage || "ok",
                        fileUrl: fileUrl,
                        senderRole: userRole,
                        time: new Date().toISOString()
                    }
                ],
                documents: []
            };

            // 3. Send reply
            const result = await services.support.replyToTicket(ticketId, payload);

            if (result.success) {
                toast.success("Reply sent successfully!");
                setReplyMessage("");
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
                // Refresh ticket data to show new message
                fetchTicket();
            } else {
                toast.error(result.message || "Failed to send reply");
            }
        } catch (err) {
            console.error("Error sending reply:", err);
            toast.error("An error occurred while sending the reply");
        } finally {
            setSending(false);
        }
    };

    const getGroupedMessages = () => {
        if (!ticketData?.description) return [];

        // 1. Flatten all messages into one array
        let allMessages = [];
        ticketData.description.forEach(group => {
            if (group.message && Array.isArray(group.message)) {
                allMessages = [...allMessages, ...group.message];
            }
        });

        // 2. Sort all messages by time
        allMessages.sort((a, b) => new Date(a.time) - new Date(b.time));

        // 3. Group by date (YYYY-MM-DD)
        const groups = {};
        allMessages.forEach(msg => {
            const dateStr = moment(msg.time).format("YYYY-MM-DD");
            if (!groups[dateStr]) {
                groups[dateStr] = [];
            }
            groups[dateStr].push(msg);
        });

        // 4. Convert back to array for rendering
        return Object.keys(groups).sort().map(date => ({
            date,
            messages: groups[date]
        }));
    };

    const formatDateLabel = (dateStr) => {
        const date = moment(dateStr);
        const today = moment().startOf('day');
        const yesterday = moment().subtract(1, 'days').startOf('day');

        if (date.isSame(today, 'd')) {
            return "TODAY";
        } else if (date.isSame(yesterday, 'd')) {
            return "YESTERDAY";
        } else {
            return date.format("D MMMM YYYY").toUpperCase();
        }
    };

    if (loading) {
        return <div className="p-4 text-center">Loading conversation...</div>;
    }

    if (error) {
        return <div className="p-4 text-red-500 text-center">{error}</div>;
    }

    if (!ticketData) {
        return <div className="p-4 text-center">No conversation data found.</div>;
    }

    const groupedMessages = getGroupedMessages();

    return (
        <div className="bg-white rounded-[6px] shadow-md p-6 mt-4 h-[calc(100vh-10rem)] overflow-hidden flex flex-col">
            <div className="border-b pb-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-3">
                        <h2 className="text-xl font-bold text-[#2d6ec7]">
                            Ticket: {ticketData.ticketNumber}
                        </h2>
                        <button
                            onClick={handleRefresh}
                            className={`p-1.5 rounded-full ${refreshing ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"} bg-gray-100 text-[#2d6ec7] transition-colors`}
                            title="Refresh Conversation"
                            disabled={refreshing}
                        >
                            <MdRefresh size={20} className={refreshing ? "animate-spin" : ""} />
                        </button>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${ticketData.ticketStatus === 'OPEN' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                        {ticketData.ticketStatus}
                    </span>
                </div>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b', fontSize: '1rem' }}>Subject: {ticketData.subject}</Typography>
                {groupedMessages?.[0]?.messages?.[0]?.message && (
                    <div className="mt-3">
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b', fontSize: '1rem' }}>Description:</Typography>
                        <div className="text-[13px] text-gray-700 bg-gray-50/80 p-3 rounded border border-gray-100 max-h-[120px] overflow-y-auto whitespace-pre-wrap break-words custom-scrollbar">
                            {groupedMessages[0].messages[0].message}
                        </div>
                    </div>
                )}
                <div className="flex gap-4 mt-3 text-sm text-gray-500">
                    <span>Priority: <span className="font-semibold text-orange-500">{ticketData.priority}</span></span>
                    <span>Created: {ticketData.createdAt ? moment(ticketData.createdAt).format("DD MMM YYYY HH:mm") : 'N/A'}</span>
                </div>
            </div>

            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto pr-2 custom-scrollbar scroll-smooth pt-2"
            >
                {groupedMessages.length > 0 ? (
                    groupedMessages.map((group, groupIdx) => (
                        <div key={groupIdx} className="mb-6">
                            <div className="flex justify-center mb-4 sticky top-0 z-10 py-2">
                                <span className="bg-[#e1f5fe]/90 backdrop-blur-sm text-[#01579b] text-[10px] font-bold px-3 py-1 rounded-full shadow-sm uppercase tracking-wider border border-[#b3e5fc]">
                                    {formatDateLabel(group.date)}
                                </span>
                            </div>
                            {group.messages.map((msg, msgIdx) => (
                                <div
                                    key={msgIdx}
                                    className={`mb-4 flex ${msg.senderRole === 'admin' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div className={`max-w-[75%] min-w-[120px] rounded-2xl p-3 shadow-sm ${msg.senderRole === 'admin'
                                        ? 'bg-[#2d6ec7] text-white rounded-br-none'
                                        : 'bg-gray-100 text-gray-800 rounded-bl-none border border-gray-200'
                                        }`}>
                                        <div className="flex justify-between items-center gap-6 mb-1 border-b border-black/5 pb-1">
                                            <span className="text-[10px] font-black uppercase tracking-tighter opacity-60">
                                                {msg.senderRole}
                                            </span>
                                            <span className="text-[9px] font-bold opacity-50">
                                                {moment(msg.time).format("hh:mm A")}
                                            </span>
                                        </div>
                                        <p className="whitespace-pre-wrap text-[13px] leading-relaxed font-medium">{msg.message}</p>
                                        {msg.fileUrl && (
                                            <div className="mt-2 pt-2 border-t border-black/5 flex flex-col gap-2">
                                                {/\.(jpg|jpeg|png|gif|webp)$/i.test(msg.fileUrl) && (
                                                    <img
                                                        src={msg.fileUrl?.startsWith('http') ? msg.fileUrl : `${process.env.REACT_APP_IMAGE_URL}/${msg.fileUrl}`}
                                                        alt="Attachment"
                                                        className="max-w-[200px] rounded-lg shadow-sm cursor-pointer hover:opacity-95 transition-opacity"
                                                        onClick={() => window.open(msg.fileUrl?.startsWith('http') ? msg.fileUrl : `${process.env.REACT_APP_IMAGE_URL}/${msg.fileUrl}`, '_blank')}
                                                    />
                                                )}
                                                <a
                                                    href={msg.fileUrl?.startsWith('http') ? msg.fileUrl : `${process.env.REACT_APP_IMAGE_URL}/${msg.fileUrl}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-2 text-[10px] font-bold hover:underline bg-black/5 px-2 py-1 rounded w-fit"
                                                >
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                                                    VIEW ATTACHMENT
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))
                ) : (
                    <div className="text-center text-gray-400 py-10 italic">
                        No messages in this conversation.
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div className="mt-4 pt-4 border-t">
                <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-2 border border-gray-200 focus-within:border-[#2d6ec7] transition-all">
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className="hidden"
                    />
                    <button
                        onClick={() => fileInputRef.current.click()}
                        className="p-2 text-gray-500 hover:text-[#2d6ec7] transition-colors"
                        title="Attach file"
                        disabled={sending}
                    >
                        <MdAttachFile size={22} className={selectedFile ? "text-[#2d6ec7]" : ""} />
                    </button>
                    <input
                        type="text"
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendReply()}
                        placeholder="Type your reply..."
                        className="flex-1 bg-transparent border-none focus:outline-none text-sm p-1"
                        disabled={sending}
                    />
                    <button
                        onClick={handleSendReply}
                        className={`p-2 rounded-full transition-all ${sending || (!replyMessage.trim() && !selectedFile)
                            ? "text-gray-300 cursor-not-allowed"
                            : "text-[#2d6ec7] hover:bg-blue-50"
                            }`}
                        disabled={sending || (!replyMessage.trim() && !selectedFile)}
                    >
                        {sending ? (
                            <div className="w-5 h-5 border-2 border-[#2d6ec7] border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                            <LuSendHorizontal size={22} />
                        )}
                    </button>
                </div>
                {selectedFile && (
                    <div className="mt-2 flex items-center gap-2 text-[10px] text-gray-500 bg-blue-50 p-1 px-2 rounded w-fit">
                        <span className="truncate max-w-[150px]">{selectedFile.name}</span>
                        <button onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }} className="text-red-500 font-bold">✕</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConversationTab;
